/**
 * INGREDIENT_INTENT — the human reason behind every source.
 *
 * Each entry tells the user WHY this specific cooperative was chosen, not
 * just what was bought. Surfaced as a "Notes from the Alchemist" hover
 * reveal next to every ingredient row in the Transparency formula ledger.
 *
 * TIME-IS-BINARY DISCIPLINE — strict rule:
 *   We record ONLY what is physically proven today. No unverified ethical
 *   claims about wages, labour audits, redistribution percentages, sapling
 *   re-plant counts, or "fair pay" promises. Sourcing intent describes the
 *   real cooperative relationship and the geography we can verify on chain
 *   today; the long-term ethical commitments live exclusively in the
 *   FoundersIntent "seed for the future" frame.
 */
export const INGREDIENT_INTENT = {
  'Kewra Water': {
    source: 'Tamil Nadu cooperative · Pandanus odorifer steam-distillation',
    intent:
      'Chosen because their distillation captures the heart of the steam — the breath of the kewra blossom — rather than the cheaper second-distillate.',
    alchemist:
      'The kewra blossom releases its perfume only when the heart of the steam is captured at the right minute. We refuse the second cut.',
  },
  'Refined Coconut Oil': {
    source: 'Cochin Cooperative · Kerala, India',
    intent:
      'Chosen because their press house cold-presses without bleach or hexane and supplies us in full traceable lots from a single kerala growing region.',
    alchemist:
      'Cold-pressed and filtered through linen, never bleached. The faint coconut whisper that remains is the proof of restraint.',
  },
  'Papaya Seed Oil': {
    source: 'Madurai Press House · Tamil Nadu, India',
    intent:
      'Chosen because they source seeds from market-stall by-streams. The seeds would otherwise be discarded; here they have a second life.',
    alchemist:
      'The seeds exfoliate only the outer keratin layer. Anything sharper is a wound, not a ritual.',
  },
  'Tomato Seed Oil': {
    source: 'Nashik Botanicals · Maharashtra, India',
    intent:
      'Chosen because they cold-press a wide cultivar mix rather than a single hybrid line — a quiet preservation of seed diversity in the Nashik valley.',
    alchemist:
      'High lycopene means the oil oxidises quickly. We blend it last, in the final hour of formulation.',
  },
  'Cashew Nut Oil': {
    source: 'Goa Cold-Press Co. · Goa, India',
    intent:
      'Chosen for a multi-year, single-press relationship that lets us specify our own oxidation tolerances and ship dates.',
    alchemist:
      'Vitamin-E rich. We chose this oil specifically because it carries through emulsification without losing its antioxidant gift.',
  },
  'Bottle Gourd Seed Oil': {
    source: 'Madurai Press House · Tamil Nadu, India',
    intent:
      'Chosen for the rotational cropping practice we have observed at this press house — a small act of soil mercy we can verify today.',
    alchemist:
      'Cooling on the skin in a way the chemistry barely explains. The Ayurveda books were right before we measured it.',
  },
  'Sea Buckthorn Oil': {
    source: 'Cooperative Surya · Uttarakhand, India',
    intent:
      'Hand-picked at first light. The Uttarakhand cooperative is the only sea-buckthorn supplier we have qualified through three full harvest cycles.',
    alchemist:
      'A drop is enough. The Omega-7 finishes the formula the way saffron finishes a basmati.',
  },
  'Pomegranate Seed Oil': {
    source: 'Anar Cooperative · Maharashtra, India',
    intent:
      'Sourced from juice-extraction by-streams. The seeds would otherwise be discarded; here they become the punicic acid heart of the lotion.',
    alchemist:
      'Punicic acid is the fingerprint. Anything synthesised loses the warmth.',
  },
  'Musk Melon Seed Oil': {
    source: 'Hyderabad Cold-Press · Telangana, India',
    intent:
      'Chosen for a seasonal supply window we accept rather than work around. Their press cycle is dictated by the harvest, not by our forecast.',
    alchemist:
      'Lightweight — sits on the skin for a moment, then disappears. The lotion needs that disappearance.',
  },
  'Prickly Pear Oil': {
    source: 'Sahara Atelier · Morocco (last imported lot, by sea)',
    intent:
      'The single non-Indian ingredient. Imported by sea, never by air, accepting the longer lead time as our climate cost.',
    alchemist:
      'It takes a tonne of cactus seeds for one litre. Use it the way you would use saffron.',
  },
  'Goji Berry Seed Oil': {
    source: 'Himalayan Berry Press · Uttarakhand, India',
    intent:
      'Chosen for the press house we have qualified across three harvests. We name the cooperative; we do not claim what they do beyond what we have witnessed.',
    alchemist:
      'Tiny dose. The radiance is in the carotenoid burst, not the volume.',
  },
  'Cloudberry Oil': {
    source: 'Cold-chain partner · Uttarakhand, India (alpine wild-harvest)',
    intent:
      'Wild-harvested under a rotational valley schedule we accept. We take thinner volumes when the schedule asks for it.',
    alchemist:
      'The Vitamin E reserve is what the formula leans on through long winters.',
  },
  'MCT Oil': {
    source: 'Cochin Cooperative · derived alongside coconut press',
    intent:
      'A by-product, not a by-purchase. Choosing the same press house keeps the supply chain at one stop.',
    alchemist:
      'Pure carrier. Lets the active oils glide instead of sit.',
  },
  'Jasmine & Saffron': {
    source: 'Cooperative Surya · Kashmir saffron + Coimbatore jasmine',
    intent:
      'Saffron sourced from a single Pampore family farm. We name the family on the box; we do not claim more than we have proven on chain.',
    alchemist:
      'Two ingredients are the perfume of the entire range. Everything else is foundation.',
  },
  'Phenoxyethanol': {
    source: 'EU pharma-grade (0.5–1.0 % only)',
    intent:
      'We name our preservative honestly and use it at the lowest defensible dose. Shelf-stability is a duty to our guests, not a vanity.',
    alchemist:
      'No paraben, no formaldehyde-donor. Phenoxyethanol does the quiet work.',
  },
  'Yogurt Powder': {
    source: 'Cultured at the Atelier · spray-dried at low temperature',
    intent:
      'Made at our own atelier from a heirloom bacterial culture, not a pharma blend. The microbiome on the scalp meets a microbiome from a kitchen.',
    alchemist:
      'Lactic acid clarity, but warm. The ferment is the difference.',
  },
  'Lauryl Glucoside': {
    source: 'European plant-derived surfactant (sugar + coconut)',
    intent:
      'Plant-derived rather than petrochemical. The unit cost is materially higher than the industry default; we accept that as the cost of the choice.',
    alchemist:
      'A surfactant should foam softly, not aggressively. This one does.',
  },
  'Onion Seed Oil': {
    source: 'Madurai Press House · Tamil Nadu, India',
    intent:
      'Chosen for a tradition Ayurveda has named for centuries. We have no claim to make beyond honouring that lineage.',
    alchemist:
      'The pungency softens within hours of formulation. The benefit remains.',
  },
  'Ojon Seed Oil': {
    source: 'Honduras rainforest cooperative · sea-imported',
    intent:
      'A second non-Indian ingredient, imported by sea. We name the country and the import method; we do not yet claim what the cooperative does locally.',
    alchemist:
      'Glossy hair from rainforest oil. We keep the dose modest.',
  },
  'Broccoli Seed Oil': {
    source: 'Pune Botanicals · Maharashtra, India',
    intent:
      'Chosen as a natural alternative to silicone shine, accepting that the rinse it leaves behind is cleaner downstream.',
    alchemist:
      'The shine is real, the rinse is clean. That is the only thing we ask of it.',
  },
  'Rice Bran Oil': {
    source: 'Punjab co-press · India',
    intent:
      'Chosen for ferulic acid content and a single co-press relationship. We name the region and the mill; we do not claim what we cannot prove.',
    alchemist:
      'Ferulic acid is delicate. We protect it from heat, from light.',
  },
  'Panthenol B5': {
    source: 'EU pharma-grade · cosmetic-purity Vitamin B5',
    intent:
      'We use the cleanest available grade and we pay for it. There is no compromise we are willing to publish on this molecule.',
    alchemist:
      'Hydration on the strand, not just at the root.',
  },
  'Watermelon Seed Oil': {
    source: 'Nashik Botanicals · Maharashtra, India',
    intent:
      'The same cooperative as our tomato seed oil. We consolidate sourcing where a single cooperative meets two needs.',
    alchemist:
      'Light. Deeply non-greasy. Carries the conditioner without weight.',
  },
  'Red Raspberry Seed Oil': {
    source: 'Himalayan Berry Press · Uttarakhand, India',
    intent:
      'Trace dose only. Selected because the omega ratio finishes the conditioner; we make no UV protection claim beyond cosmetic shine.',
    alchemist:
      'A whisper. Anything more would compete with the oils that come before it.',
  },
  'Quat Guar Gum': {
    source: 'European pharma-grade · cationic conditioner',
    intent:
      'A controversial but functional cationic. We accept the trade-off in exchange for using zero silicone.',
    alchemist:
      'The conditioning sensation people expect. Sourced honestly.',
  },
  'Trace fragrance carrier': {
    source: 'Atelier blend · jasmine, kewra, saffron carrier',
    intent:
      'The remainder. Declared rather than buried in “fragrance”. We tell you the carrier exists; you can decide.',
    alchemist:
      'Nothing exotic. Just the discipline of declaring every percent.',
  },
  'Emulsifying Wax': {
    source: 'Plant-derived emulsifying complex · cetyl + stearyl alcohol',
    intent:
      'The most boring line on the spec sheet, and the most honest. An emulsion needs a wax. We chose the cleanest plant version available.',
    alchemist:
      'Stabiliser. Without it the lotion separates within a week.',
  },
};

/**
 * getIntent(name) — robust lookup; returns null when no entry exists,
 * letting the UI quietly hide the Intent reveal rather than show a blank.
 */
export const getIntent = (name) => INGREDIENT_INTENT[name] || null;
